import { useQueryClient } from '@tanstack/react-query'
import { Card, Divider, Empty, Flex } from 'antd'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'

import {
  useUserServiceGetMessagesCountEndpointKey,
  useUserServiceGetMessagesListEndpointKey,
  useUserServiceMessageMarkReadEndpoint
} from 'src/api/queries'
import { GetMessagesListResponseItem } from 'src/api/requests'
import { NotificationCard, Text } from 'src/components'
import { yearMonthDaySlash, dayStringMonthYearFormat } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type NotificationsListProps = {
  listData?: GetMessagesListResponseItem[]
}

const groupNotificationsByDay = (
  notifications: GetMessagesListResponseItem[]
) => {
  return notifications.reduce(
    (dates, notification) => {
      const date = dayjs(notification.createdOn).format(yearMonthDaySlash)

      if (!dates[date]) {
        dates[date] = []
      }
      dates[date].push(notification)

      return dates
    },
    {} as Record<string, GetMessagesListResponseItem[]>
  )
}

const formatNotificationDate = (
  dateString: Date | string,
  t: (val: string) => string,
  currentLang: string
) => {
  const notificationDate = dayjs(dateString)

  if (notificationDate.isToday()) return t('common.today')

  if (notificationDate.isYesterday()) return t('common.yesterday')

  return notificationDate.locale(currentLang).format(dayStringMonthYearFormat)
}

export const NotificationsList = ({ listData }: NotificationsListProps) => {
  const { t, currentLang } = useLocales()
  const palette = useTheme()
  const { styles } = useStyles()
  const queryClient = useQueryClient()

  const { mutate } = useUserServiceMessageMarkReadEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useUserServiceGetMessagesListEndpointKey]
      })
      queryClient.invalidateQueries({
        queryKey: [useUserServiceGetMessagesCountEndpointKey]
      })
    }
  })

  const onRead = (notification: GetMessagesListResponseItem) => {
    if (!notification.read) {
      mutate({ id: String(notification.id) })
    }
  }

  const groupedNotifications = groupNotificationsByDay(listData || [])

  return (
    <Card bordered={false}>
      {listData?.length ? (
        <Flex vertical gap={20}>
          {Object.entries(groupedNotifications).map(([key, value]) => (
            <Flex vertical key={key}>
              <Text
                color={palette.grey.grey1}
                weight={700}
                className={styles.text}
              >
                {formatNotificationDate(key, t, currentLang.value)}
              </Text>

              <Divider className={styles.divider} />

              <Flex vertical gap={20}>
                {value.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    title={notification?.title}
                    content={notification?.text}
                    type={notification?.senderType}
                    time={notification?.createdOn}
                    isRead={notification?.read}
                    onClick={() => onRead(notification)}
                  />
                ))}
              </Flex>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Empty
          className={styles.empty}
          description={t('notifications.zero_notifications')}
        />
      )}
    </Card>
  )
}
